import React, { useMemo, useRef, useState, useCallback, DragEventHandler } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Button, Upload } from 'antd';
import { GlobalVariable, ImageSettings } from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import globalVariablesManager from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import { DataType, SupportedTypes } from '@kemu-io/hs-types';
import styles from '../GlobalVariablesPanel.module.css';
import classNames from 'classnames';

type Props = {
  variable: GlobalVariable;
  onChange: (name: string,value: SupportedTypes, type: DataType) => void;
}

const canvasImageDataToImgElement = (imageData: ImageData) => {
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const ctx = canvas.getContext('2d');
  ctx?.putImageData(imageData, 0, 0);
  return canvas.toDataURL();
};

/**
 * Read a File object as a data URL using FileReader.
 *
 * @param file The file to read
 * @returns A data URL string for the file contents
 */
const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });

/**
 * Create an HTMLImageElement and resolve when it has loaded.
 *
 * @param src The image URL or data URL to load
 * @returns The loaded image element
 */
const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });

const imageToImageData = (image: HTMLImageElement): ImageData | null => {
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext('2d');
  if(!ctx) {
    return null;
  }

  ctx.drawImage(image, 0, 0);
  const data = ctx.getImageData(0, 0, image.width, image.height);
  return data;
};

/**
 * Creates a copy of ImageData
 * @param imageData - The ImageData to copy
 * @returns A new ImageData instance with copied data
 */
const copyImageData = (imageData: ImageData): ImageData => {
  return new ImageData(
    new Uint8ClampedArray(imageData.data),
    imageData.width,
    imageData.height
  );
};

/**
 * Image control component for global variables
 * @param variable - The global variable configuration
 * @param onChange - Callback when value changes
 */
const ImageControl = ({ variable, onChange }: Props) => {
  const settings = variable.controlSettings as ImageSettings;
  const currentValue = variable.lastValue as ImageData | null;
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropActive, setDropActive] = useState(false);

  // Convert the ImageData type to a img tag element
  const imgBase64 = useMemo(() => {
    if(currentValue){
      // const img = new Image();
      const dataUrl = canvasImageDataToImgElement(currentValue);
      return dataUrl;
    }

    return null;
  }, [currentValue]);

  /**
   * Process file upload (both from click and drag-drop)
   */
  const processFile = useCallback(async (file: File) => {
    const base64String = await readFileAsDataURL(file);
    const image = await loadImage(base64String);
    const imageData = imageToImageData(image);
    if(imageData) {
      onChange(variable.name, imageData, DataType.ImageData);
    }
  }, [variable.name, onChange]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  /**
   * Handle custom upload request from Ant Design Upload component
   */
  const handleCustomRequest = useCallback(async (options: any) => {
    const file = options.file as File;
    await processFile(file);
    setDropActive(false);
  }, [processFile]);

  /**
   * Handle drag enter event
   */
  const handleDragEnter: DragEventHandler = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setDropActive(true);
  };

  /**
   * Handle drag leave event
   */
  const handleDragLeave: DragEventHandler = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setDropActive(false);
  };

  /**
   * Handle drag start on image preview
   * Sets the variable name in dataTransfer so we can identify the source
   */
  const handleImageDragStart: DragEventHandler = (event) => {
    if (currentValue && imgBase64) {
      event.dataTransfer.setData('application/kemu-image-variable', variable.name);
      event.dataTransfer.effectAllowed = 'copy';
      // Use the already rendered image element as drag image
      const imgElement = event.currentTarget as HTMLImageElement;
      if (event.dataTransfer.setDragImage && imgElement.complete) {
        const dragImageSize = Math.min(Math.max(imgElement.width, imgElement.height), 100);
        const canvas = document.createElement('canvas');
        canvas.width = dragImageSize;
        canvas.height = dragImageSize;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Draw scaled version of the image for drag preview
          ctx.drawImage(imgElement, 0, 0, dragImageSize, dragImageSize);
          event.dataTransfer.setDragImage(canvas, dragImageSize / 2, dragImageSize / 2);
        }
      }
    }
  };

  /**
   * Handle drop event
   * Checks if dropped item is from another variable and copies the ImageData
   */
  const handleDrop: DragEventHandler = async (event) => {
    event.stopPropagation();
    event.preventDefault();
    setDropActive(false);

    // Check if this is a drag from another image variable
    const sourceVariableName = event.dataTransfer.getData('application/kemu-image-variable');
    
    if (sourceVariableName && sourceVariableName !== variable.name) {
      // Get the source variable
      const sourceVariable = globalVariablesManager.getGlobalVariable(sourceVariableName);
      if (sourceVariable && sourceVariable.lastValue && sourceVariable.lastDataType === DataType.ImageData) {
        const sourceImageData = sourceVariable.lastValue as ImageData;
        // Copy the ImageData
        const copiedImageData = copyImageData(sourceImageData);
        onChange(variable.name, copiedImageData, DataType.ImageData);
        return;
      }
    }

    // Handle file drop (existing behavior)
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        await processFile(file);
      }
    }
  };

  if (imgBase64) {
    return (
      <div className={styles.FileControlContainer}>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.dataTransfer.types.includes('application/kemu-image-variable')) {
              setDropActive(true);
            } else {
              handleDragEnter(e);
            }
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.dataTransfer.types.includes('application/kemu-image-variable')) {
              setDropActive(true);
            } else {
              handleDragEnter(e);
            }
          }}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Upload
            listType="text"
            className={classNames(styles.ControlFile, styles.DropZone, {
              [styles.Active]: dropActive,
            })}
            accept="image/*"
            showUploadList={false}
            customRequest={handleCustomRequest}
            openFileDialogOnClick={false}
          >
            <div className={styles.ControlFile}>
              <img 
                src={imgBase64 ?? undefined} 
                alt="Variable image" 
                className={styles.ControlImage}
                draggable={true}
                onDragStart={handleImageDragStart}
                style={{ cursor: 'grab' }}
              />
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
          </Upload>
        </div>
        <Button 
          type="text" 
          className={styles.ControlFileButton}
          onClick={() => inputRef.current?.click()}
        >
          Change
        </Button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types.includes('application/kemu-image-variable')) {
          setDropActive(true);
        } else {
          handleDragEnter(e);
        }
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types.includes('application/kemu-image-variable')) {
          setDropActive(true);
        } else {
          handleDragEnter(e);
        }
      }}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Upload
        listType="text"
        className={classNames(styles.ControlFile, styles.Empty, styles.DropZone, {
          [styles.Active]: dropActive,
        })}
        accept="image/*"
        showUploadList={false}
        customRequest={handleCustomRequest}
        openFileDialogOnClick={false}
      >
        <div>
          <UploadOutlined className={styles.ControlFileIcon} />
          <Button 
            type="text" 
            className={styles.ControlFileButton}
            onClick={() => inputRef.current?.click()}
          >
            Upload Image
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>
      </Upload>
    </div>
  );
};

export default ImageControl;
